import { Injectable, Injector, inject } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { MbsSize, ModalService, SidepanelService, ToastService } from 'mbs-ui-kit';
import { AgentUpdateRequiredModalComponent } from 'mbs-ui-shared@components/computers-modal/agent-update-required-modal/agent-update-required-modal.component';
import { ComputersAsyncResultsModalComponent } from 'mbs-ui-shared@components/computers-modal/async-results-modal/computers-async-results-modal.component';
import { BackupAgentUpdateRequiredModalComponent } from 'mbs-ui-shared@components/computers-modal/backup-agent-update-required-modal/backup-agent-update-required-modal.component';
import { DeepInstinctModalComponent } from 'mbs-ui-shared@components/deep-instinct-modal/deep-instinct-modal.component';
import { SidepanelDownloadsComponent } from 'mbs-ui-shared@components/sidepanel-downloads/sidepanel-downloads.component';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { ComputerModalsFacade } from 'mbs-ui-shared@facades/computer.modals.facade';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import { Administrator } from 'mbs-ui-shared@models';
import Computer, { AgentType } from 'mbs-ui-shared@models/Computer';
import { getNeededBuildType } from 'mbs-ui-shared@models/InstallAgent';
import { BuildService } from 'mbs-ui-shared@services/build-service/build.service';
import { handleRMMError } from 'mbs-ui-shared@utils/handleError';
import { EMPTY, Observable, from, noop, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';

const backupWithTokenSupportsAgentVersion = 770;
const backupMacWithTokenSupportsAgentVersion = 440;
const linuxBackupSupportsAgentInstall = 421106;

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class ComputerAgentWrapper {
  private facade = inject(ComputersFacade);
  private modals = inject(ComputerModalsFacade);
  private sidepanelService = inject(SidepanelService);
  private buildService = inject(BuildService);
  private modalService = inject(ModalService);
  private injector = inject(Injector);
  private i18nPipe = inject(I18NextPipe);
  private auth = inject(AuthFacade);
  private toastService = inject(ToastService);

  private downloadPanel = SidepanelDownloadsComponent;

  public currentUser: Administrator;

  constructor() {
    this.auth.currentUser$.pipe(filter<Administrator>(Boolean), untilDestroyed(this)).subscribe((user) => (this.currentUser = user));
  }

  handleInstallAgent(computer: Computer, agent: AgentType): void {
    if (
      Computer.isUnix(computer) &&
      agent === AgentType.RMM &&
      Computer.hasAgent(computer, AgentType.Backup) &&
      !Computer.IsSupportedAgentVersion(computer, AgentType.Backup, linuxBackupSupportsAgentInstall, true)
    ) {
      this.modalService.openCustom(AgentUpdateRequiredModalComponent, {
        data: {
          computer
        }
      });

      return;
    }

    const isSupportedAgentVersion = Computer.IsSupportedAgentVersion(
      computer,
      AgentType.Backup,
      Computer.isMac(computer) ? backupMacWithTokenSupportsAgentVersion : backupWithTokenSupportsAgentVersion
    );

    if (agent === AgentType.RA && Computer.hasAgent(computer, AgentType.Backup) && !isSupportedAgentVersion) {
      this.modalService.openCustom(BackupAgentUpdateRequiredModalComponent, {
        data: {
          os: computer?.os,
          computerName: Computer.getComputerName(computer)
        }
      });

      return;
    }

    const checkBuildAndInstall = () =>
      this.checkPublicBuild(computer, agent).pipe(
        switchMap((resultAgent) => this.facade.installAgent(computer, resultAgent)),
        switchMap(() => this.modals.openInstallationStartedModal(agent, computer))
      );

    if ([AgentType.Backup, AgentType.RMM].includes(agent)) {
      this.modals
        .openInstallAgentModal(agent as AgentType.Backup | AgentType.RMM, computer)
        .pipe(
          catchError(() => EMPTY),
          switchMap(() => checkBuildAndInstall())
        )
        .subscribe();
    } else if (agent === AgentType.RA) {
      checkBuildAndInstall().subscribe();
    } else if (agent === AgentType.DeepInst) {
      this.installDeepInstinct(computer);
    }
  }

  handleGroupInstallAgent(computers: Computer[], agent: AgentType) {
    const request = this.facade.installAgentToComputers(computers, agent, true).pipe(
      tap(() => {
        this.modals.showInstallationCompletedToast(agent);
        this.facade.invalidate();
      })
    );

    this.facade
      .handleGroupOperation(request, {
        operationText: this.modals.getInstallationTitle(agent),
        modalOperationTitle: this.modals.getInstallationTitle(agent),
        isGroupAction: true,
        detailModal: ComputersAsyncResultsModalComponent
      })
      .pipe(take(1))
      .subscribe();
  }

  handleBulkInstallAgent(agent: AgentType, skipPaging = false) {
    this.modals
      .openBulkInstallAgentModal(agent as AgentType.Backup | AgentType.RMM | AgentType.RA)
      .pipe(
        switchMap(() => this.facade.currentPageRequestParams$(false, skipPaging)),
        take(1),
        switchMap((filters) => {
          const request = this.facade.bulkInstallAgent(filters, agent, true).pipe(
            tap(() => {
              this.modals.showInstallationCompletedToast(agent);
              this.facade.invalidate();
            })
          );

          return this.facade
            .handleGroupOperation(request, {
              operationText: this.modals.getInstallationTitle(agent),
              modalOperationTitle: this.modals.getInstallationTitle(agent),
              isGroupAction: true,
              detailModal: ComputersAsyncResultsModalComponent
            })
            .pipe(take(1));
        })
      )
      .subscribe();
  }

  private installDeepInstinct(computer: Computer) {
    this.facade.applicationsCountAfterInit$
      .pipe(
        take(1),
        switchMap((installedApps) =>
          installedApps.deepInst ? this.installDeepInstinctAgent(computer) : this.openDeepInstinctModal(computer)
        ),
        untilDestroyed(this)
      )
      .subscribe();

    this.facade.loadApplicationsCount();
  }

  private installDeepInstinctAgent(computer: Computer): Observable<any> {
    return this.facade.installAgent(computer, AgentType.DeepInst).pipe(
      tap((result) => result?.isErrorsInResultList && handleRMMError(result.resultList, this.toastService)),
      switchMap((result) =>
        result?.isErrorsInResultList ? of(result) : this.modals.openInstallationStartedModal(AgentType.DeepInst, computer)
      )
    );
  }

  private openDeepInstinctModal(computer: Computer): Observable<unknown> {
    return from(
      this.modalService
        .openCustom(DeepInstinctModalComponent, {
          data: {
            title: this.i18nPipe.transform('shared:deepInstinctModal:title', {
              format: 'title'
            }),
            computer,
            userId: this.currentUser?.Id
          },
          size: MbsSize.lg
        })
        .catch(noop)
    );
  }

  handleOpenDownloadPanel(): void {
    this.sidepanelService.addAndOpenByType(this.downloadPanel, null, this.injector).pipe(untilDestroyed(this)).subscribe();
  }

  private checkPublicBuild(computer: Computer, agent: AgentType): Observable<AgentType> {
    return this.getFirstPublicBuildApplicationAsAgentType(computer, agent).pipe(
      switchMap((agent) =>
        (agent
          ? of(false)
          : this.modals.openNoPublicBuildsModal().pipe(
              tap((result) => !result && this.handleOpenDownloadPanel()),
              map(() => true)
            )
        ).pipe(
          filter((res) => !res),
          map(() => agent)
        )
      )
    );
  }

  private getFirstPublicBuildApplicationAsAgentType(computer: Computer, agent: AgentType): Observable<AgentType | null> {
    return this.getPublicBuild(computer, agent).pipe(map((builds) => (builds?.[0]?.applicationId as AgentType) ?? null));
  }

  private getPublicBuild(computer: Computer, agent: AgentType) {
    const buildType = getNeededBuildType(computer, agent);
    return this.buildService
      .getAll()
      .pipe(
        map((builds) =>
          builds && builds.builds && Array.isArray(builds.builds)
            ? builds.builds.filter((build) => build.mbsBuildType === buildType && build.public)
            : []
        )
      );
  }
}
